import type { AmplitudeIdentify } from '../types/amplitude.js';
import { getTrackingData } from '../cookie/tracking-cookie.js';
import { getTaleezUser } from '../cookie/taleez-user.js';
import { logger } from '../utils/logger.js';

/**
 * Construit un objet AmplitudeIdentify avec les bons setOnce / set.
 *
 * Scope (décision PO — 14/04/2026) :
 * - Amplitude gère déjà les UTMs, click IDs et page views via son
 *   autocapture.attribution natif. On ne les duplique PAS ici.
 * - Trackeez pousse uniquement à Amplitude :
 *   - `email` (set)
 *   - Propriétés `taleez_*` si le cookie `taleez_user` est présent
 *     (setOnce pour les immuables, set pour le reste)
 *
 * Les données d'attribution (UTMs, click IDs, page views, etc.) continuent
 * d'être envoyées à HubSpot via `_hsq identify`.
 *
 * Prérequis : window.amplitude doit être chargé et initialisé.
 */
export function buildAmplitudeIdentifyCall(): AmplitudeIdentify {
  if (!window.amplitude) {
    throw new Error('[Trackeez] buildAmplitudeIdentifyCall appelé sans window.amplitude');
  }

  const data = getTrackingData();
  const identify = new window.amplitude.Identify();

  // Debug parallèle — reflète ce qu'on met dans l'Identify
  const debugSet: Record<string, string | number> = {};
  const debugSetOnce: Record<string, string | number> = {};

  // Helpers pour factoriser set / setOnce + debug
  const doSet = (key: string, value: string | number | undefined): void => {
    if (value === undefined || value === '' || value === null) return;
    identify.set(key, value);
    debugSet[key] = value;
  };
  const doSetOnce = (key: string, value: string | number | undefined): void => {
    if (value === undefined || value === '' || value === null) return;
    identify.setOnce(key, value);
    debugSetOnce[key] = value;
  };

  // Email
  const email = data.email_identify_hs ?? data.email;
  doSet('email', email);

  // Taleez User (utilisateur connecté à app.taleez.com)
  const taleezUser = getTaleezUser();
  if (taleezUser) {
    logger.info('Taleez user détecté:', taleezUser.mail);
    doSetOnce('taleez_user_id', taleezUser.id);
    doSet('taleez_email', taleezUser.mail);
    doSetOnce('taleez_company_id', taleezUser.companyId);
    doSetOnce('taleez_account_created_at', taleezUser.dateCreation);
    doSet('taleez_account_last_modif', taleezUser.dateLastModif);
    if (taleezUser.deleted !== undefined)  doSet('taleez_account_deleted', taleezUser.deleted ? 1 : 0);
    if (taleezUser.disabled !== undefined) doSet('taleez_account_disabled', taleezUser.disabled ? 1 : 0);
    if (taleezUser.role) doSet('taleez_role', taleezUser.role);
  }

  logger.info('Amplitude identify construit', { set: debugSet, setOnce: debugSetOnce });
  logger.table({ ...debugSetOnce, ...debugSet });

  return identify;
}
